import axios, { AxiosInstance, AxiosError } from 'axios';

// API URL - use environment variable or default to relative path (nginx proxy)
// If VITE_API_URL is empty, use relative paths so nginx proxy handles /api requests
// @ts-ignore - Vite environment variable
const API_URL = import.meta.env?.VITE_API_URL || '';

console.log('API URL:', API_URL);

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Request interceptor - add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const fullUrl = config.baseURL ? `${config.baseURL}${config.url}` : config.url;
    console.log('API Request:', config.method?.toUpperCase(), fullUrl, 'Base URL:', config.baseURL || '(empty - using relative)');
    return config;
  },
  (error) => {
    console.error('API Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors and refresh token when needed
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.config.url);
    return response;
  },
  async (error: AxiosError<any>) => {
    console.error('API Response error:', error.response?.status, error.config?.url);
    console.error('Error details:', error.response?.data || error.message);
    console.error('Error code:', error.code);
    console.error('Error config:', error.config);
    
    // Если нет ответа от сервера (сетевая ошибка)
    if (!error.response) {
      console.error('Network error - no response from server');
      console.error('Request URL:', error.config?.url || 'unknown');
      console.error('Base URL:', error.config?.baseURL || 'unknown');
      const fullUrl = error.config?.baseURL && error.config?.url 
        ? error.config.baseURL + error.config.url 
        : 'unknown';
      console.error('Full URL would be:', fullUrl);
      
      // Показываем более понятное сообщение об ошибке
      const networkError = new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      (networkError as any).isNetworkError = true;
      return Promise.reject(networkError);
    }
    
    const originalRequest: any = error.config;

    // Если получили 401 и это не повторный запрос на refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Ждём завершения текущего refresh и повторяем запрос
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: unknown) => {
              if (typeof token === 'string' && originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              resolve(api(originalRequest));
            },
            reject,
          });
        });
      }

      isRefreshing = true;

      try {
        // Use relative path for refresh endpoint (nginx proxy will handle it)
        const refreshUrl = API_URL ? `${API_URL}/api/auth/refresh` : '/api/auth/refresh';
        const response = await axios.post(
          refreshUrl,
          { refreshToken },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        const newAccessToken = response.data.accessToken as string;
        const newRefreshToken = response.data.refreshToken as string;

        localStorage.setItem('accessToken', newAccessToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        processQueue(null, newAccessToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        return api(originalRequest);
      } catch (refreshError: any) {
        processQueue(refreshError, null);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;


